"use client"

import { useState } from "react"
import { Box, Button, Card, CardContent, CircularProgress, Grid, TextField, Typography } from "@mui/material"
import { useNotification } from "../../contexts/NotificationContext"
import { changePassword } from "../../services/auth.service"

const MIN_PASSWORD_LENGTH = 6
const EMPTY_FORM = { currentPassword: "", newPassword: "", confirmPassword: "" }

const ChangePasswordCard = () => {
  const { showNotification } = useNotification()
  const [formData, setFormData] = useState(EMPTY_FORM)
  const [saving, setSaving] = useState(false)

  const handleChange = (event) => {
    const { name, value } = event.target
    setFormData((previous) => ({ ...previous, [name]: value }))
  }

  const newPasswordError =
    formData.newPassword && formData.newPassword.length < MIN_PASSWORD_LENGTH
      ? `At least ${MIN_PASSWORD_LENGTH} characters`
      : ""
  const confirmError =
    formData.confirmPassword && formData.confirmPassword !== formData.newPassword ? "Passwords do not match" : ""
  const canSubmit =
    formData.currentPassword &&
    formData.newPassword.length >= MIN_PASSWORD_LENGTH &&
    formData.confirmPassword === formData.newPassword

  const handleSubmit = async (event) => {
    event.preventDefault()
    setSaving(true)
    try {
      await changePassword(formData.currentPassword, formData.newPassword)
      showNotification("Password changed successfully", "success")
      setFormData(EMPTY_FORM)
    } catch (error) {
      showNotification(error.message || "Failed to change password", "error")
    } finally {
      setSaving(false)
    }
  }

  return (
    <Card sx={{ mt: 3 }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Change password
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Enter your current password, then choose a new one. If you have forgotten your current password, sign out
          and use "Forgot password" on the login page.
        </Typography>
        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                type="password"
                label="Current password"
                name="currentPassword"
                autoComplete="current-password"
                value={formData.currentPassword}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                type="password"
                label="New password"
                name="newPassword"
                autoComplete="new-password"
                value={formData.newPassword}
                onChange={handleChange}
                error={Boolean(newPasswordError)}
                helperText={newPasswordError}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                type="password"
                label="Confirm new password"
                name="confirmPassword"
                autoComplete="new-password"
                value={formData.confirmPassword}
                onChange={handleChange}
                error={Boolean(confirmError)}
                helperText={confirmError}
              />
            </Grid>
          </Grid>
          <Box display="flex" justifyContent="flex-end" sx={{ mt: 3 }}>
            <Button
              type="submit"
              variant="contained"
              disabled={!canSubmit || saving}
              startIcon={saving && <CircularProgress size={20} color="inherit" />}
            >
              {saving ? "Changing..." : "Change password"}
            </Button>
          </Box>
        </form>
      </CardContent>
    </Card>
  )
}

export default ChangePasswordCard
